import { useCallback, useEffect, useRef, useState } from 'react';

// Gates contract acceptance on having seen the bottom of the document. A short
// document may fit the scroll box without overflowing, so the scroll event never
// fires — in that case there is nothing left to scroll, so treat it as read.
// The ResizeObserver re-checks when the box or its content changes size
// (breakpoint height change, images inside the document finishing loading).
export const useContractScrollGate = (documentHtml: string | undefined) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

    const checkIsAtBottom = useCallback(() => {
        const element = containerRef.current;
        if (element && element.scrollHeight - element.scrollTop - element.clientHeight < 50) {
            setHasScrolledToBottom(true);
        }
    }, []);

    useEffect(() => {
        const element = containerRef.current;
        if (!element || !documentHtml) return;

        setHasScrolledToBottom(false);
        checkIsAtBottom();
        const observer = new ResizeObserver(checkIsAtBottom);
        observer.observe(element);
        Array.from(element.children).forEach(child => observer.observe(child));

        return () => observer.disconnect();
    }, [documentHtml, checkIsAtBottom]);

    return { containerRef, hasScrolledToBottom, handleScroll: checkIsAtBottom };
};
